import { t } from "../i18n/strings";
import { getDateTime } from "../utils/time";
import type { TicketMessage } from "../api/types";

interface TicketThreadProps {
  messages: TicketMessage[];
}

const AUTHOR_LABELS = {
  client: "thread.client",
  support: "thread.support",
  bot: "thread.bot",
} as const;

const DELIVERY_LABELS = {
  delivered: "thread.delivered",
  pending: "thread.pending",
  failed: "thread.failed",
} as const;

function getText(message: TicketMessage): string {
  if (message.kind === "attachment") {
    return t("thread.attachment", { type: message.content_type ?? "?" });
  }
  return message.text ?? "";
}

/**
 * The conversation, oldest first, with the two sides visually apart.
 *
 * An answer telegram refused is shown and not hidden: it is what somebody wrote, and the
 * reason it did not arrive is the only way to tell "nobody answered" from "the client blocked
 * the bot". A `pending` one is shown as unknown rather than sent - the api wrote it before
 * trying, so it means the send never finished.
 */
export function TicketThread({ messages }: TicketThreadProps) {
  if (messages.length === 0) {
    return <p className="thread__empty">{t("thread.empty")}</p>;
  }

  return (
    <ol className="thread">
      {messages.map((message) => (
        <li
          key={message.id}
          className={`thread__message thread__message--${message.author}${
            message.delivery_status === "failed" ? " thread__message--failed" : ""
          }`}
        >
          <div className="thread__meta">
            <span className="thread__author">{t(AUTHOR_LABELS[message.author])}</span>
            <span className="thread__time">{getDateTime(message.created_at)}</span>
          </div>

          <p className="thread__text">{getText(message)}</p>

          {message.delivery_status && message.delivery_status !== "delivered" ? (
            <p className="thread__delivery">
              {t(DELIVERY_LABELS[message.delivery_status])}
              {message.status_code ? ` (${message.status_code})` : ""}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
